import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  Renderer2,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { UserEnvService } from '@core/services/user-env/user-env.service';
import { resolveBootstrapColWidths } from '../../utils/bootstrap-col-widths';
import {
  getUserDateFormat,
  momentFormatToPlaceholder,
  momentFormatToPrimeNg,
} from '../../utils/kl-date-format';

@Component({
  selector: 'kl-date-picker',
  standalone: false,
  templateUrl: './kl-date-picker.html',
  styleUrl: './kl-date-picker.scss',
})
export class KlDatePickerComponent implements OnInit {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly userEnv = inject(UserEnvService);

  readonly formGroup = input.required<FormGroup>();
  readonly controlName = input.required<string>();

  readonly label = input('');
  readonly labelDescription = input('');
  /** Custom i18n placeholder key; when empty, shows locale date-format hint. */
  readonly placeholder = input('');
  /** i18n key for moment-style parse pattern (`date_format_pattern` in lang files). */
  readonly dateFormatPatternKey = input('date_format_pattern');
  /** i18n key for localized empty-state hint (`date_format_placeholder` in lang files). */
  readonly dateFormatPlaceholderKey = input('date_format_placeholder');
  /** PrimeNG date pattern; omit to fall back to the user's own configured date format. */
  readonly dateFormat = input<string | undefined>(undefined);
  /** Optional placeholder override (display text, e.g. `gg.aa.yyyy`). */
  readonly datePlaceholder = input<string | undefined>(undefined);
  readonly showTime = input(false);
  readonly showSeconds = input(false);
  readonly timeOnly = input(false);
  readonly hourFormat = input<'12' | '24'>('24');
  readonly showIcon = input(true);
  /** When `showIcon` is true, `'input'` places the icon inside the field (PrimeNG `iconDisplay`). */
  readonly iconDisplay = input<'button' | 'input'>('button');
  readonly showClear = input(false);
  readonly showOnFocus = input(true);
  readonly appendTo = input<unknown>('body');
  readonly readonlyInput = input(false);
  readonly minDate = input<Date | undefined>(undefined);
  readonly maxDate = input<Date | undefined>(undefined);
  readonly fluid = input(true);

  readonly col = input('12');
  readonly sm = input<string | undefined>(undefined);
  readonly md = input<string | undefined>(undefined);
  readonly lg = input<string | undefined>(undefined);
  readonly xl = input<string | undefined>(undefined);
  readonly marginY = input(false);
  readonly marginBottom = input(true);

  readonly showFieldError = input(false);
  readonly inputId = input('');

  readonly valueChange = output<unknown>();

  private readonly resolvedMomentPattern = signal(getUserDateFormat());
  private readonly resolvedPlaceholder = signal(momentFormatToPlaceholder(getUserDateFormat()));

  readonly control = computed<AbstractControl | null>(() => this.formGroup()?.get(this.controlName()) ?? null);

  /**
   * `control.invalid`/`.touched`/`.errors` are plain mutable state, not signals, so a `computed()`
   * reading them never re-runs on its own — e.g. `form.markAllAsTouched()` from a save handler
   * wouldn't turn on this field's red border. Bumped on every control event (see constructor) so
   * the validation-derived computeds below stay live.
   */
  private readonly controlState = signal(0);

  /** PrimeNG pattern from `dateFormat` input or the resolved moment pattern. */
  readonly effectiveDateFormat = computed<string>(
    () => this.dateFormat() ?? momentFormatToPrimeNg(this.resolvedMomentPattern()),
  );

  readonly effectiveDatePlaceholder = computed(() => this.datePlaceholder() ?? this.resolvedPlaceholder());

  readonly displayPlaceholder = computed(() => {
    const custom = this.placeholder();
    if (custom) {
      return this.translate.instant(custom);
    }
    if (this.timeOnly()) {
      return this.datePlaceholder() ?? (this.hourFormat() === '12' ? 'hh:mm' : 'HH:mm');
    }
    return this.effectiveDatePlaceholder();
  });

  readonly fieldId = computed(() => (this.inputId() || this.controlName() || 'kl-date-picker').replace(/\s/g, '_'));

  readonly required = computed(() => {
    this.controlState();
    const c = this.control();
    if (!c?.validator) return false;
    const v = c.validator({} as AbstractControl);
    return !!v?.['required'];
  });

  readonly showInvalid = computed(() => {
    this.controlState();
    const c = this.control();
    return !!(c && c.invalid && c.touched);
  });

  readonly controlError = computed(() => {
    this.controlState();
    const c = this.control();
    if (!c?.invalid || !c.touched) return null;
    if (c.errors?.['required']) return 'This field is required.';
    return null;
  });

  constructor() {
    effect(() => {
      this.userEnv.env();
      this.refreshDatePattern();
    });

    effect((onCleanup) => {
      const sub = this.control()?.events.subscribe(() => this.controlState.update((v) => v + 1));
      onCleanup(() => sub?.unsubscribe());
    });

    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.refreshDatePattern();
    });
  }

  ngOnInit(): void {
    this.refreshDatePattern();

    const el = this.host.nativeElement;
    const col = this.col();
    const { sm, md, lg, xl } = resolveBootstrapColWidths(col, this.sm(), this.md(), this.lg(), this.xl());
    this.renderer.addClass(el, 'col-' + col);
    this.renderer.addClass(el, 'col-sm-' + sm);
    this.renderer.addClass(el, 'col-md-' + md);
    this.renderer.addClass(el, 'col-lg-' + lg);
    this.renderer.addClass(el, 'col-xl-' + xl);
  }

  private refreshDatePattern(): void {
    if (!this.dateFormat()) {
      const envFmt = this.userEnv.env()?.date_format;
      if (envFmt) {
        this.resolvedMomentPattern.set(envFmt);
      } else {
        const patternKey = this.dateFormatPatternKey();
        const translatedPattern = this.translate.instant(patternKey);
        if (translatedPattern && translatedPattern !== patternKey) {
          this.resolvedMomentPattern.set(translatedPattern);
        } else {
          this.resolvedMomentPattern.set(getUserDateFormat());
        }
      }
    }

    const placeholderKey = this.dateFormatPlaceholderKey();
    const translatedPlaceholder = this.translate.instant(placeholderKey);
    if (translatedPlaceholder && translatedPlaceholder !== placeholderKey) {
      this.resolvedPlaceholder.set(translatedPlaceholder);
    } else {
      this.resolvedPlaceholder.set(momentFormatToPlaceholder(this.resolvedMomentPattern()));
    }
  }

  onSelect(): void {
    this.valueChange.emit(this.control()?.value);
  }
}
